import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { NetworkError, requestPasswordReset } from '../services/authService';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const canSubmit = useMemo(() => {
    return email.trim().length > 0;
  }, [email]);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;
    try {
      setIsSubmitting(true);
      setErrorMessage(null);
      setSuccessMessage(null);
      const result = await requestPasswordReset(email, `${window.location.origin}/reset-password`);
      setSuccessMessage(result.message || 'Reset link sent. Check your inbox.');
    } catch (error) {
      if (error instanceof NetworkError) {
        setErrorMessage(error.message);
      } else if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('Unable to send reset link.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fbfd] px-6 py-10">
      <div className="mx-auto w-full max-w-[560px] rounded-2xl border border-[#dbe9f1] bg-white p-8 shadow-sm">
        <h1 className="text-[34px] font-extrabold text-[#0c5870]">Forgot Password</h1>
        <p className="mt-2 text-[15px] font-medium text-[#6a8799]">
          Enter your work email and we will send a recovery link.
        </p>
        <form className="mt-8 space-y-5" onSubmit={onSubmit}>
          <div>
            <label htmlFor="email" className="mb-2 block text-[13px] font-extrabold uppercase tracking-[0.12em] text-[#8ca9ba]">
              Work Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="h-12 w-full rounded-[12px] border border-[#c8dbe7] px-4 text-[15px]"
              placeholder="name@company.com"
            />
          </div>

          {errorMessage ? <p className="rounded-md bg-red-50 px-3 py-2 text-[13px] text-red-600">{errorMessage}</p> : null}
          {successMessage ? <p className="rounded-md bg-emerald-50 px-3 py-2 text-[13px] text-emerald-700">{successMessage}</p> : null}

          <button
            type="submit"
            disabled={!canSubmit || isSubmitting}
            className="h-12 w-full rounded-full bg-[#0c8dab] text-[15px] font-extrabold text-white disabled:opacity-60"
          >
            {isSubmitting ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>
        <div className="mt-6 text-center">
          <Link to="/login" className="text-[14px] font-extrabold text-[#1f8baa]">Back to Login</Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
